import type { OwnerReview, VehicleRating } from '~/contracts/vdp';
import type { RatingCounts } from '~/services/vdp/contracts';

function completeAndSortRatingDistributions(ratingCounts: RatingCounts[]) {
  return [1, 2, 3, 4, 5]
    .map(key => {
      const existingRating = ratingCounts.find(rating => rating.key === key);

      return existingRating || { key, doc_count: 0 };
    })
    .sort((a, b) => b.key - a.key);
}

export default function calculateRatingsAndDistribution(
  ratingCounts: RatingCounts[],
  reviews: OwnerReview[] = []
): VehicleRating {
  const completedSortedRatingDistributions =
    completeAndSortRatingDistributions(ratingCounts);

  const ratingsWithReviews = new Set(
    reviews
      .map(review => review.reviewRating)
      .filter(
        (rating): rating is number => rating !== null && rating !== undefined
      )
  );

  const { sumOfRatings, totalRatingCounts } =
    completedSortedRatingDistributions.reduce(
      (acc, rating) => {
        acc.sumOfRatings += rating.key * rating.doc_count;
        acc.totalRatingCounts += rating.doc_count;

        return acc;
      },
      { sumOfRatings: 0, totalRatingCounts: 0 }
    );

  const averageRating =
    totalRatingCounts > 0
      ? Math.ceil((sumOfRatings / totalRatingCounts) * 10) / 10
      : 0;

  const ratingDistribution = completedSortedRatingDistributions.map(rating => ({
    rating: rating.key,
    count: rating.doc_count,
    percentage:
      totalRatingCounts > 0
        ? Math.round((rating.doc_count / totalRatingCounts) * 1000) / 10
        : 0,
    hasReviewsForThisRating: ratingsWithReviews.has(rating.key)
  }));

  return {
    averageRating,
    totalRatingCounts,
    ratingDistribution
  };
}
